import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  MinusCircleIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import {
  getCasheaSummary,
  getCasheaCycles,
  getCasheaCycleDetail,
  getCasheaInvoices,
  registerCasheaCyclePayment,
  deleteCasheaCyclePayment,
  getCasheaConfig,
  updateCasheaConfig,
  setCasheaCycleRate,
  clearCasheaCycleRate,
  runCasheaSync,
  getTenantBanks,
  getLegacyCasheaSales,
  markLegacyCasheaSalePaid,
  deleteLegacyCasheaSale,
} from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtVES = (v) =>
  v === null || v === undefined
    ? '—'
    : `Bs ${Number(v).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtUSD = (v) =>
  v === null || v === undefined
    ? '—'
    : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateLong = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long' });
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-VE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const fmtRate = (r) => (r ? Number(r).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '—');

const RATE_SOURCE = {
  manual:  { label: 'tasa fijada para este miércoles', color: 'text-purple-700' },
  dia:     { label: 'tasa registrada ese día', color: 'text-green-700' },
  vigente: { label: 'proyección con la última tasa', color: 'text-gray-500' },
  none:    { label: 'sin tasa', color: 'text-red-600' },
};

function RateNote({ cycle }) {
  const src = RATE_SOURCE[cycle.payout_rate_source] || RATE_SOURCE.none;
  return (
    <span className={`text-xs ${src.color}`}>
      {fmtUSD(cycle.net_usd)} × {fmtRate(cycle.payout_rate)} · {src.label}
    </span>
  );
}

const STATUS = {
  en_curso:     { label: 'En curso',      color: 'bg-sky-100 text-sky-800',       Icon: ClockIcon,               icon: 'text-sky-500' },
  por_cobrar:   { label: 'Por cobrar',    color: 'bg-yellow-100 text-yellow-800', Icon: BanknotesIcon,           icon: 'text-yellow-600' },
  vencido:      { label: 'Vencido',       color: 'bg-red-100 text-red-800',       Icon: ExclamationTriangleIcon, icon: 'text-red-500' },
  parcial:      { label: 'Pago parcial',  color: 'bg-orange-100 text-orange-800', Icon: ExclamationTriangleIcon, icon: 'text-orange-500' },
  pagado:       { label: 'Pagado',        color: 'bg-green-100 text-green-800',   Icon: CheckCircleIcon,         icon: 'text-green-500' },
  sin_facturas: { label: 'Sin facturas',  color: 'bg-gray-100 text-gray-600',     Icon: MinusCircleIcon,         icon: 'text-gray-400' },
};

function StatusBadge({ status }) {
  const cfg = STATUS[status] || STATUS.sin_facturas;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}>
      <Icon className={`w-3.5 h-3.5 ${cfg.icon}`} />
      {cfg.label}
    </span>
  );
}

function Card({ label, value, sub, colorClass = 'bg-white border-gray-200 text-gray-900' }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${colorClass}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-0.5">{label}</p>
      <p className="text-xl font-black">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

function InitialBreakdown({ items }) {
  if (!items || items.length === 0) return <span className="text-gray-400 text-xs">Sin inicial</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((i, idx) => (
        <span key={idx} className="inline-block text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">
          {i.label}: {i.currency === 'US' || /USD/.test(i.method) ? fmtUSD(i.amount_usd) : fmtVES(i.amount_ves)}
        </span>
      ))}
    </div>
  );
}

// ─── Tabla de facturas (reutilizada en ciclo y en pestaña Facturas) ──────────

function InvoicesTable({ invoices, showCycle = false }) {
  if (!invoices || invoices.length === 0) {
    return <p className="text-center py-8 text-gray-500">No hay facturas Cashea en este rango</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tienda</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Doc.</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total factura</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Inicial (tienda)</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Financiado Cashea</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Comisión</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Neto a recibir</th>
            {showCycle && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cobro / cuenta</th>}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {invoices.map((inv) => {
            const annulled = inv.status === 'anulada';
            return (
              <tr key={inv.id} className={annulled ? 'opacity-50 line-through' : ''}>
                <td className="px-3 py-2 whitespace-nowrap">
                  {fmtDate(inv.date)} <span className="text-gray-400 text-xs">{inv.time}</span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{inv.store_name}</td>
                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                  {inv.tipodoc} {inv.document}
                  {annulled && <span className="ml-1 no-underline text-red-600 font-semibold">ANULADA</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{inv.client_name}</div>
                  <div className="text-xs text-gray-500">{inv.client_id}</div>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div>{fmtVES(inv.invoice_total_ves)}</div>
                  <div className="text-xs text-gray-500">{fmtUSD(inv.invoice_total_usd)}</div>
                </td>
                <td className="px-3 py-2"><InitialBreakdown items={inv.initial_breakdown} /></td>
                <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-blue-700">
                  <div>{fmtVES(inv.cashea_ves)}</div>
                  <div className="text-xs text-blue-500 font-normal">{fmtUSD(inv.cashea_usd)}</div>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-red-600">
                  -{fmtVES(inv.commission_ves)}
                  <div className="text-xs text-gray-400">{inv.commission_pct}%</div>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap font-bold text-green-700">
                  <div>{fmtVES(inv.net_ves)}</div>
                  <div className="text-xs text-green-600 font-normal">{fmtUSD(inv.net_usd)}</div>
                </td>
                {showCycle && (
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    Mié {fmtDate(inv.payment_date)}
                    {inv.paid && <CheckCircleIcon className="inline w-4 h-4 text-green-500 ml-1" />}
                    <div className="text-gray-500">{inv.account_group_label || inv.account_group}</div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Modal de registro de pago recibido ──────────────────────────────────────

function PaymentModal({ cycle, banks, onClose, onSaved }) {
  const [form, setForm] = useState({
    payment_date: todayISO(),
    amount_ves: Math.max(0, (cycle.expected_payout_ves || 0) - (cycle.received_ves || 0)).toFixed(2),
    exchange_rate: cycle.payout_rate ? String(cycle.payout_rate) : '',
    bank: '',
    reference: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const amount = parseFloat(form.amount_ves);
    if (!amount || amount <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }
    try {
      setSaving(true);
      await registerCasheaCyclePayment(cycle.payment_date, {
        account_group: cycle.account_group,
        payment_date: form.payment_date,
        amount_ves: amount,
        exchange_rate: form.exchange_rate ? parseFloat(form.exchange_rate) : null,
        bank: form.bank || null,
        reference: form.reference || null,
        notes: form.notes || null,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const rateNow = parseFloat(form.exchange_rate) || cycle.payout_rate || 0;
  const expectedNow = rateNow > 0 ? (cycle.net_usd || 0) * rateNow : (cycle.expected_payout_ves || 0);
  const pendingVes = Math.max(0, expectedNow - (cycle.received_ves || 0));
  const diff = parseFloat(form.amount_ves || 0) - pendingVes;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <form onSubmit={submit} className="p-6 space-y-4">
          <h3 className="text-lg font-bold">💰 Registrar pago recibido de Cashea</h3>
          <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between"><span>Cuenta</span><span className="font-semibold">{cycle.account_group_label}</span></div>
            <div className="flex justify-between"><span>Ciclo</span><span className="font-medium">{fmtDate(cycle.cycle_start)} → {fmtDate(cycle.cycle_end)}</span></div>
            <div className="flex justify-between"><span>Fecha de pago acordada</span><span className="font-medium">Mié {fmtDate(cycle.payment_date)}</span></div>
            <div className="flex justify-between"><span>Financiado ({cycle.invoice_count} fact.)</span><span>{fmtVES(cycle.cashea_ves)}</span></div>
            <div className="flex justify-between text-red-600"><span>Comisión</span><span>-{fmtVES(cycle.commission_ves)}</span></div>
            <div className="flex justify-between"><span>Neto en dólares</span><span className="font-semibold">{fmtUSD(cycle.net_usd)}</span></div>
            <div className="flex justify-between font-bold text-green-700"><span>A depositar ({fmtUSD(cycle.net_usd)} × {fmtRate(rateNow)})</span><span>{fmtVES(expectedNow)}</span></div>
            {cycle.received_ves > 0 && (
              <div className="flex justify-between text-orange-700"><span>Ya recibido</span><span>{fmtVES(cycle.received_ves)}</span></div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha en que se recibió</label>
              <input type="date" value={form.payment_date} required
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Monto recibido (Bs)</label>
              <input type="number" step="0.01" min="0" value={form.amount_ves} required
                onChange={(e) => setForm({ ...form, amount_ves: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-semibold" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Banco receptor</label>
              <input list="cashea-banks" value={form.bank}
                onChange={(e) => setForm({ ...form, bank: e.target.value })}
                placeholder="Banesco, BNC, BDV…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              <datalist id="cashea-banks">
                {banks.map((b) => <option key={b} value={b} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Referencia</label>
              <input value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tasa que aplicó Cashea</label>
              <input type="number" step="0.0001" value={form.exchange_rate}
                onChange={(e) => {
                  const r = parseFloat(e.target.value);
                  const exp = r > 0 ? (cycle.net_usd || 0) * r - (cycle.received_ves || 0) : null;
                  setForm({ ...form, exchange_rate: e.target.value, amount_ves: exp !== null ? Math.max(0, exp).toFixed(2) : form.amount_ves });
                }}
                placeholder="Bs por USD"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notas</label>
              <input value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
          </div>

          {Math.abs(diff) > 0.01 && (
            <p className={`text-sm ${diff < 0 ? 'text-orange-700' : 'text-blue-700'}`}>
              {diff < 0
                ? `Quedarían pendientes ${fmtVES(-diff)} (pago parcial).`
                : `El monto supera lo esperado por ${fmtVES(diff)}.`}
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400">
              {saving ? 'Guardando…' : 'Confirmar pago'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Detalle de ciclo (expandible) ───────────────────────────────────────────

function CycleDetail({ cycle, storeId, isAdmin, banks, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPay, setShowPay] = useState(false);
  const [rateInput, setRateInput] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  const saveRate = async () => {
    const r = parseFloat(rateInput);
    if (!r || r <= 0) { alert('Tasa inválida'); return; }
    try {
      setSavingRate(true);
      await setCasheaCycleRate(cycle.payment_date, r);
      setRateInput('');
      await load();
      onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSavingRate(false);
    }
  };

  const resetRate = async () => {
    if (!window.confirm('¿Quitar la tasa fijada y volver a la tasa del módulo de Tasa de Cambio?')) return;
    try {
      await clearCasheaCycleRate(cycle.payment_date);
      await load();
      onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setDetail(await getCasheaCycleDetail(cycle.payment_date, cycle.account_group, storeId));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [cycle.payment_date, cycle.account_group, storeId]);

  useEffect(() => { load(); }, [load]);

  const removePayment = async (p) => {
    if (!window.confirm(`¿Eliminar el pago de ${fmtVES(p.amount_ves)} del ${fmtDate(p.payment_date)}?`)) return;
    try {
      await deleteCasheaCyclePayment(p.id);
      await load();
      onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  if (loading || !detail) return <div className="px-6 py-4 text-sm text-gray-500">Cargando…</div>;
  const c = detail.cycle;

  return (
    <div className="px-4 md:px-6 py-4 bg-gray-50/70 space-y-4">
      {/* Por tienda */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Por tienda</p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 uppercase">
                <th className="text-left py-1 pr-4">Tienda</th>
                <th className="text-right py-1 px-2">Fact.</th>
                <th className="text-right py-1 px-2">Inicial cliente</th>
                <th className="text-right py-1 px-2">Financiado</th>
                <th className="text-right py-1 px-2">Comisión</th>
                <th className="text-right py-1 pl-2">Neto</th>
              </tr>
            </thead>
            <tbody>
              {detail.by_store.map((s) => (
                <tr key={s.store_id} className="border-t border-gray-200">
                  <td className="py-1 pr-4">{s.store_name}</td>
                  <td className="text-right py-1 px-2">{s.invoice_count}</td>
                  <td className="text-right py-1 px-2 text-gray-500">{fmtVES(s.initial_ves)}</td>
                  <td className="text-right py-1 px-2 text-blue-700">{fmtVES(s.cashea_ves)}</td>
                  <td className="text-right py-1 px-2 text-red-600">-{fmtVES(s.commission_ves)}</td>
                  <td className="text-right py-1 pl-2 font-semibold text-green-700">{fmtUSD(s.net_usd)} <span className="text-xs text-gray-400">({fmtVES(s.net_ves)} al día de venta)</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tasa del miércoles y monto real a depositar */}
      {!storeId && (
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-purple-700">Monto real a depositar el mié {fmtDate(c.payment_date)}</p>
            <p className="text-xl font-black text-purple-900">{fmtVES(c.expected_payout_ves)}</p>
            <RateNote cycle={c} />
          </div>
          <div className="flex items-center gap-2">
            <input type="number" step="0.0001" min="0" value={rateInput} onChange={(e) => setRateInput(e.target.value)}
              placeholder={`Tasa del ${fmtDate(c.payment_date)}`}
              className="w-40 px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
            <button onClick={saveRate} disabled={savingRate} className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs font-semibold hover:bg-purple-700 disabled:bg-gray-400">
              Fijar tasa
            </button>
            {c.payout_rate_source === 'manual' && (
              <button onClick={resetRate} className="text-xs text-gray-500 hover:text-red-600 underline">quitar</button>
            )}
          </div>
        </div>
      )}

      {/* Pagos recibidos */}
      {!storeId && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Pagos recibidos de Cashea · {c.account_group_label}</p>
            {!c.is_open && c.status !== 'pagado' && (
              <button onClick={() => setShowPay(true)} className="px-3 py-1.5 bg-green-600 text-white rounded-md text-xs font-semibold hover:bg-green-700">
                💰 Registrar pago recibido
              </button>
            )}
          </div>
          {c.payments.length === 0 ? (
            <p className="text-sm text-gray-500">
              {c.is_open ? 'El ciclo sigue abierto; el pago se registra a partir del miércoles.' : 'Todavía no se ha registrado ningún pago para este ciclo.'}
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <tbody>
                {c.payments.map((p) => (
                  <tr key={p.id} className="border-t border-gray-200">
                    <td className="py-1 pr-4">{fmtDate(p.payment_date)}</td>
                    <td className="py-1 pr-4 font-semibold">{fmtVES(p.amount_ves)} {p.amount_usd && <span className="text-xs text-gray-400 font-normal">({fmtUSD(p.amount_usd)} @ {p.exchange_rate})</span>}</td>
                    <td className="py-1 pr-4 text-gray-600">{p.bank || '—'} {p.reference && <span className="font-mono text-xs">#{p.reference}</span>}</td>
                    <td className="py-1 pr-4 text-gray-500 text-xs">{p.notes} · {p.user_name}</td>
                    <td className="py-1 text-right">
                      {isAdmin && (
                        <button onClick={() => removePayment(p)} className="text-red-500 hover:text-red-700" title="Eliminar pago">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {c.received_ves > 0 && (
            <p className={`text-sm mt-2 font-semibold ${Math.abs(c.difference_ves) < 1 ? 'text-green-700' : c.difference_ves < 0 ? 'text-orange-700' : 'text-blue-700'}`}>
              Diferencia vs. monto a depositar ({fmtVES(c.expected_payout_ves)}): {c.difference_ves >= 0 ? '+' : ''}{fmtVES(c.difference_ves)}
            </p>
          )}
        </div>
      )}

      {/* Facturas */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
          Facturas ({detail.invoices.length}{detail.annulled_count > 0 && `, ${detail.annulled_count} anuladas`})
        </p>
        <div className="bg-white rounded-lg border border-gray-200">
          <InvoicesTable invoices={detail.invoices} />
        </div>
      </div>

      {showPay && (
        <PaymentModal
          cycle={c}
          banks={banks}
          onClose={() => setShowPay(false)}
          onSaved={async () => { setShowPay(false); await load(); onChanged(); }}
        />
      )}
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

const Cashea = () => {
  const { stores } = useStores();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);

  // Resumen
  const [summary, setSummary] = useState(null);

  // Ciclos
  const [cycles, setCycles] = useState([]);
  const [cycleStore, setCycleStore] = useState('');
  const [cycleGroup, setCycleGroup] = useState('');
  const [cycleGroups, setCycleGroups] = useState([]);
  const [expanded, setExpanded] = useState(null);   // `${payment_date}|${account_group}`

  // Facturas
  const [invFilters, setInvFilters] = useState({ startDate: '', endDate: '', storeId: '', accountGroup: '', q: '', includeAnnulled: false });
  const [invData, setInvData] = useState({ invoices: [], totals: null });

  // Config
  const [config, setConfig] = useState(null);
  const [pctInput, setPctInput] = useState('');
  const [groupsDraft, setGroupsDraft] = useState([]);
  const [syncLog, setSyncLog] = useState([]);
  const [syncing, setSyncing] = useState(false);

  // Legado
  const [legacy, setLegacy] = useState([]);
  const [legacyPay, setLegacyPay] = useState(null);
  const [legacyForm, setLegacyForm] = useState({ payment_date: todayISO(), payment_amount_ves: '', payment_exchange_rate: '' });

  useEffect(() => {
    getTenantBanks()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.banks || [];
        setBanks(list.map((b) => b.label || b.name || b.key).filter(Boolean));
      })
      .catch(() => setBanks(['Banesco', 'BNC', 'BDV', 'Banplus']));
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setSummary(await getCasheaSummary());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCycles = useCallback(async () => {
    try {
      setLoading(true);
      const d = await getCasheaCycles(cycleStore || null, cycleGroup || null);
      setCycles(d.cycles);
      setCycleGroups(d.account_groups || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [cycleStore, cycleGroup]);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setInvData(await getCasheaInvoices(invFilters));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [invFilters]);

  const loadConfig = useCallback(async () => {
    try {
      const c = await getCasheaConfig();
      setConfig(c);
      setPctInput(String(c.commission_pct));
      setGroupsDraft((c.account_groups || []).map((g) => ({ ...g, store_ids: [...(g.store_ids || [])] })));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadLegacy = useCallback(async () => {
    try {
      setLoading(true);
      setLegacy(await getLegacyCasheaSales('pending'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'summary') loadSummary();
    if (tab === 'cycles') loadCycles();
    if (tab === 'invoices') loadInvoices();
    if (tab === 'config' || tab === 'invoices') loadConfig();
    if (tab === 'legacy') loadLegacy();
  }, [tab, cycleStore, cycleGroup, loadSummary, loadCycles, loadInvoices, loadConfig, loadLegacy]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const r = await runCasheaSync();
      setSyncLog(r.log || []);
      if (tab === 'summary') loadSummary();
      if (tab === 'cycles') loadCycles();
      if (tab === 'invoices') loadInvoices();
      if (tab === 'config') loadConfig();
    } catch (e) {
      alert('Error al sincronizar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    const pct = parseFloat(pctInput);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      alert('Porcentaje inválido');
      return;
    }
    if (!window.confirm(`¿Cambiar la comisión Cashea a ${pct}%? Se recalcularán las facturas de ciclos no pagados.`)) return;
    try {
      const r = await updateCasheaConfig({ commission_pct: pct });
      alert(`Comisión actualizada. Facturas recalculadas: ${r.recalculated_invoices}`);
      loadConfig();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const toggleStoreInGroup = (groupKey, storeId) => {
    setGroupsDraft((prev) => prev.map((g) => {
      if (g.is_default) return { ...g, store_ids: [] };
      const has = g.store_ids.includes(storeId);
      if (g.key === groupKey) return { ...g, store_ids: has ? g.store_ids.filter((s) => s !== storeId) : [...g.store_ids, storeId] };
      // una tienda solo puede estar en un grupo no-default
      return { ...g, store_ids: g.store_ids.filter((s) => s !== storeId) };
    }));
  };

  const saveGroups = async () => {
    if (!window.confirm('¿Guardar la asignación de tiendas por cuenta? Se reasignarán las facturas ya sincronizadas.')) return;
    try {
      const r = await updateCasheaConfig({ account_groups: groupsDraft });
      alert(`Cuentas guardadas. Facturas reasignadas: ${r.reassigned_invoices}`);
      loadConfig();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const submitLegacyPayment = async () => {
    try {
      await markLegacyCasheaSalePaid(legacyPay.id, {
        payment_date: legacyForm.payment_date,
        payment_amount_ves: parseFloat(legacyForm.payment_amount_ves),
        payment_exchange_rate: parseFloat(legacyForm.payment_exchange_rate),
      });
      setLegacyPay(null);
      loadLegacy();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const deleteLegacy = async (id) => {
    if (!window.confirm('¿Eliminar este registro manual?')) return;
    try {
      await deleteLegacyCasheaSale(id);
      loadLegacy();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const tabs = [
    { key: 'summary', label: '📊 Resumen' },
    { key: 'cycles', label: '📅 Ciclos de pago' },
    { key: 'invoices', label: '🧾 Facturas' },
    ...(isAdmin ? [{ key: 'config', label: '⚙️ Configuración' }] : []),
    { key: 'legacy', label: '🗂️ Registros manuales' },
  ];

  const lastSync = summary?.sync_state?.last_run || config?.sync_state?.last_run;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">💳 Cashea</h1>
          <p className="text-gray-600 text-sm">
            Facturas con método Cashea traídas automáticamente de Tecnodroid. Lo facturado de lunes a domingo lo paga Cashea el miércoles siguiente, menos su comisión.
          </p>
          {lastSync && <p className="text-xs text-gray-400 mt-1">Última sincronización: {fmtDateTime(lastSync)}</p>}
        </div>
        <button onClick={handleSync} disabled={syncing}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 text-sm font-semibold">
          <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
        </button>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-x-6">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${tab === t.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Resumen ─────────────────────────────────────────────────────── */}
      {tab === 'summary' && summary && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card
              label={`Ciclo vigente · ${fmtDate(summary.current_cycle_dates.cycle_start)} → ${fmtDate(summary.current_cycle_dates.cycle_end)}`}
              value={fmtVES(summary.totals.current_cashea_ves)}
              sub={`${summary.totals.current_invoice_count} facturas financiadas (todas las cuentas)`}
              colorClass="bg-sky-50 border-sky-200 text-sky-900"
            />
            <Card
              label={`A depositar el mié ${fmtDate(summary.current_cycle_dates.payment_date)}`}
              value={fmtVES(summary.totals.current_expected_payout_ves)}
              sub={`${fmtUSD(summary.totals.current_net_usd)} neto × tasa ${fmtRate(summary.totals.current_payout_rate)} (${RATE_SOURCE[summary.totals.current_payout_rate_source]?.label}) · comisión ${summary.commission_pct}%`}
              colorClass="bg-green-50 border-green-200 text-green-900"
            />
            <Card
              label="Pendiente acumulado (ciclos cerrados)"
              value={fmtVES(summary.totals.pending_net_ves)}
              sub={`${summary.totals.pending_cycles_count} ciclos sin cobrar en total`}
              colorClass={summary.totals.pending_cycles_count > 0 ? 'bg-yellow-50 border-yellow-200 text-yellow-900' : 'bg-white border-gray-200 text-gray-900'}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {summary.accounts.map((a) => {
              const c = a.current_cycle;
              const np = a.next_payout;
              return (
                <div key={a.key} className="bg-white rounded-lg shadow p-5 space-y-4 border-t-4 border-blue-500">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h3 className="text-lg font-bold">🏦 {a.label}</h3>
                      <p className="text-xs text-gray-500">
                        {a.is_default ? 'Todas las tiendas no asignadas a otra cuenta' : `${a.store_ids.length} tiendas`}
                        {' · '}{a.paid_cycles_count} ciclos cobrados · {a.pending_cycles_count} pendientes
                      </p>
                    </div>
                    <button onClick={() => { setTab('cycles'); setCycleGroup(a.key); setExpanded(null); }}
                      className="text-sm text-blue-600 hover:underline">Ver ciclos →</button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2">
                      <p className="text-xs uppercase font-semibold text-sky-700">Ciclo vigente · {fmtDate(c.cycle_start)} → {fmtDate(c.cycle_end)}</p>
                      <p className="text-lg font-black text-sky-900">{fmtVES(c.cashea_ves)}</p>
                      <p className="text-xs text-sky-700">{c.invoice_count} facturas · neto <b>{fmtUSD(c.net_usd)}</b></p>
                      <p className="text-sm font-bold text-green-800 mt-1">A depositar: {fmtVES(c.expected_payout_ves)}</p>
                      <p className="text-xs text-sky-700">Mié {fmtDate(c.payment_date)} · tasa {fmtRate(c.payout_rate)}</p>
                    </div>
                    <div className={`rounded-lg border px-3 py-2 ${np ? (np.status === 'vencido' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200') : 'bg-gray-50 border-gray-200'}`}>
                      <p className="text-xs uppercase font-semibold text-gray-600">Próximo cobro pendiente</p>
                      {np ? (
                        <>
                          <p className="text-lg font-black">{fmtVES(np.expected_payout_ves - np.received_ves)}</p>
                          <p className="text-xs text-gray-600">{fmtUSD(np.net_usd)} × {fmtRate(np.payout_rate)} · Mié {fmtDate(np.payment_date)} · <StatusBadge status={np.status} /></p>
                          <p className="text-xs text-gray-600">{np.invoice_count} fact. · ciclo {fmtDate(np.cycle_start)} → {fmtDate(np.cycle_end)}</p>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 mt-1">Nada pendiente de cobro</p>
                      )}
                      {a.pending_cycles_count > 1 && (
                        <p className="text-xs font-semibold text-gray-700 mt-1">Acumulado: {fmtVES(a.pending_net_ves)} en {a.pending_cycles_count} ciclos</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-1"><CalendarDaysIcon className="w-4 h-4" /> Por día</p>
                      {a.current_by_day.length === 0 ? <p className="text-sm text-gray-400">Sin facturas aún.</p> : (
                        <table className="min-w-full text-sm">
                          <tbody>
                            {a.current_by_day.map((d) => (
                              <tr key={d.date} className="border-t border-gray-100">
                                <td className="py-1 capitalize">{fmtDateLong(d.date)}</td>
                                <td className="py-1 text-right text-gray-500">{d.invoice_count}</td>
                                <td className="py-1 text-right font-semibold text-blue-700">{fmtVES(d.cashea_ves)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Por tienda</p>
                      {a.current_by_store.length === 0 ? <p className="text-sm text-gray-400">Sin datos.</p> : (
                        <table className="min-w-full text-sm">
                          <tbody>
                            {a.current_by_store.map((s) => (
                              <tr key={s.store_id} className="border-t border-gray-100">
                                <td className="py-1 text-xs">{s.store_name}</td>
                                <td className="py-1 text-right text-gray-500">{s.invoice_count}</td>
                                <td className="py-1 text-right font-semibold text-green-700">{fmtVES(s.net_ves)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  {np && (
                    <div className="flex justify-end">
                      <button onClick={() => { setTab('cycles'); setCycleGroup(a.key); setExpanded(`${np.payment_date}|${a.key}`); }}
                        className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700">
                        Registrar pago recibido
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {tab === 'summary' && !summary && <p className="text-center py-8 text-gray-500">Cargando…</p>}

      {/* ── Ciclos ──────────────────────────────────────────────────────── */}
      {tab === 'cycles' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Ciclos de pago (miércoles)</h2>
            <div className="flex flex-wrap gap-2">
              <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm">
                <button onClick={() => { setCycleGroup(''); setExpanded(null); }}
                  className={`px-3 py-2 ${cycleGroup === '' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Todas las cuentas</button>
                {cycleGroups.map((g) => (
                  <button key={g.key} onClick={() => { setCycleGroup(g.key); setExpanded(null); }}
                    className={`px-3 py-2 border-l border-gray-300 ${cycleGroup === g.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>🏦 {g.label}</button>
                ))}
              </div>
              <select value={cycleStore} onChange={(e) => { setCycleStore(e.target.value); setExpanded(null); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm">
                <option value="">Todas las tiendas</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          {cycleStore && (
            <p className="px-4 py-2 text-xs text-gray-500 bg-gray-50">Filtrando por tienda: los pagos recibidos se registran a nivel de cuenta completa, no por tienda.</p>
          )}
          {loading && cycles.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Cargando…</p>
          ) : cycles.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No hay facturas Cashea sincronizadas todavía.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-8" />
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pago (miércoles)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cuenta</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Facturado</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fact.</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Financiado</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Comisión</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Neto USD</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">A depositar (Bs)</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Recibido</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cycles.map((c) => {
                    const key = `${c.payment_date}|${c.account_group}`;
                    const open = expanded === key;
                    return (
                      <React.Fragment key={key}>
                        <tr onClick={() => setExpanded(open ? null : key)} className="cursor-pointer hover:bg-gray-50">
                          <td className="pl-3">{open ? <ChevronDownIcon className="w-4 h-4 text-gray-400" /> : <ChevronRightIcon className="w-4 h-4 text-gray-400" />}</td>
                          <td className="px-3 py-2 font-semibold whitespace-nowrap">{fmtDate(c.payment_date)}</td>
                          <td className="px-3 py-2 whitespace-nowrap"><span className="inline-block px-2 py-0.5 rounded bg-blue-50 text-blue-800 text-xs font-semibold">🏦 {c.account_group_label}</span></td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtDate(c.cycle_start)} → {fmtDate(c.cycle_end)}</td>
                          <td className="px-3 py-2 text-right">{c.invoice_count}</td>
                          <td className="px-3 py-2 text-right text-blue-700 whitespace-nowrap">{fmtVES(c.cashea_ves)}<div className="text-xs text-gray-400">{fmtUSD(c.cashea_usd)}</div></td>
                          <td className="px-3 py-2 text-right text-red-600 whitespace-nowrap">-{fmtVES(c.commission_ves)}</td>
                          <td className="px-3 py-2 text-right font-bold whitespace-nowrap">{fmtUSD(c.net_usd)}<div className="text-xs text-gray-400 font-normal">{fmtVES(c.net_ves)} día venta</div></td>
                          <td className="px-3 py-2 text-right font-bold text-green-700 whitespace-nowrap">{fmtVES(c.expected_payout_ves)}<div className={`text-xs font-normal ${(RATE_SOURCE[c.payout_rate_source] || RATE_SOURCE.none).color}`}>× {fmtRate(c.payout_rate)} {c.payout_rate_source === 'manual' ? '(fijada)' : c.payout_rate_source === 'dia' ? '(del día)' : '(proy.)'}</div></td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {c.received_ves > 0 ? fmtVES(c.received_ves) : <span className="text-gray-400">—</span>}
                            {c.received_ves > 0 && Math.abs(c.difference_ves) >= 1 && (
                              <div className={`text-xs ${c.difference_ves < 0 ? 'text-orange-600' : 'text-blue-600'}`}>{c.difference_ves > 0 ? '+' : ''}{fmtVES(c.difference_ves)}</div>
                            )}
                          </td>
                          <td className="px-3 py-2"><StatusBadge status={c.status} /></td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={11} className="p-0">
                              <CycleDetail cycle={c} storeId={cycleStore || null} isAdmin={isAdmin} banks={banks} onChanged={loadCycles} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Facturas ────────────────────────────────────────────────────── */}
      {tab === 'invoices' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
              <input type="date" value={invFilters.startDate} onChange={(e) => setInvFilters({ ...invFilters, startDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
              <input type="date" value={invFilters.endDate} onChange={(e) => setInvFilters({ ...invFilters, endDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tienda</label>
              <select value={invFilters.storeId} onChange={(e) => setInvFilters({ ...invFilters, storeId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                <option value="">Todas</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Cuenta</label>
              <select value={invFilters.accountGroup} onChange={(e) => setInvFilters({ ...invFilters, accountGroup: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                <option value="">Todas</option>
                {(config?.account_groups || cycleGroups).map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Cliente / cédula / documento</label>
              <input value={invFilters.q} onChange={(e) => setInvFilters({ ...invFilters, q: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && loadInvoices()} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Buscar…" />
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                <input type="checkbox" checked={invFilters.includeAnnulled} onChange={(e) => setInvFilters({ ...invFilters, includeAnnulled: e.target.checked })} /> Anuladas
              </label>
              <button onClick={loadInvoices} className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
                <MagnifyingGlassIcon className="w-4 h-4" /> Buscar
              </button>
            </div>
          </div>
          {invData.totals && invData.invoices.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 text-sm flex flex-wrap gap-x-6 gap-y-1 border-b border-gray-200">
              <span>{invData.totals.count} facturas</span>
              <span>Inicial en tienda: <b>{fmtVES(invData.totals.initial_ves)}</b></span>
              <span>Financiado Cashea: <b className="text-blue-700">{fmtVES(invData.totals.cashea_ves)}</b> ({fmtUSD(invData.totals.cashea_usd)})</span>
              <span>Neto a recibir: <b className="text-green-700">{fmtVES(invData.totals.net_ves)}</b> ({fmtUSD(invData.totals.net_usd)})</span>
            </div>
          )}
          {loading ? <p className="text-center py-8 text-gray-500">Cargando…</p> : <InvoicesTable invoices={invData.invoices} showCycle />}
        </div>
      )}

      {/* ── Configuración ───────────────────────────────────────────────── */}
      {tab === 'config' && isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Comisión Cashea</h2>
            <form onSubmit={saveConfig} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Porcentaje que Cashea descuenta sobre el monto financiado</label>
                <div className="flex items-center gap-2">
                  <input type="number" step="0.01" min="0" max="100" value={pctInput} onChange={(e) => setPctInput(e.target.value)} className="w-32 px-3 py-2 border border-gray-300 rounded-md" />
                  <span className="text-gray-600">%</span>
                </div>
                {config?.updated_at && <p className="text-xs text-gray-400 mt-1">Última modificación: {fmtDateTime(config.updated_at)}</p>}
              </div>
              <p className="text-xs text-gray-500">Al cambiarlo se recalculan comisión y neto de todas las facturas cuyo ciclo aún no está pagado. Los ciclos ya pagados no se tocan.</p>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Guardar</button>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-2">Sincronización Tecnodroid</h2>
            <p className="text-sm text-gray-600 mb-3">
              Corre automáticamente cada 15 minutos. Trae de <code>opercli</code> toda factura con método de pago <b>CASHEA</b> y guarda como deuda solo ese monto; los demás métodos quedan como inicial del cliente.
            </p>
            {config?.sync_state?.last_run && (
              <ul className="text-sm text-gray-700 space-y-1 mb-3">
                <li>Última corrida: <b>{fmtDateTime(config.sync_state.last_run)}</b></li>
                <li>Ventana: {fmtDate(config.sync_state.window_from)} → {fmtDate(config.sync_state.window_to)}</li>
                <li>Nuevas: {config.sync_state.last_inserted} · Actualizadas: {config.sync_state.last_updated}</li>
              </ul>
            )}
            <button onClick={handleSync} disabled={syncing} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 text-sm">
              <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
            </button>
            {syncLog.length > 0 && (
              <pre className="mt-3 text-xs bg-gray-900 text-green-300 rounded p-3 overflow-x-auto max-h-64">{syncLog.join('\n')}</pre>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold mb-1">Cuentas donde deposita Cashea</h2>
            <p className="text-sm text-gray-600 mb-4">
              Cada cuenta lleva su propio ciclo de cobro y sus propios pagos recibidos. Marca las tiendas que pertenecen a cada cuenta; las que no se marquen caen en la cuenta principal.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {groupsDraft.map((g, gi) => (
                <div key={g.key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span>🏦</span>
                    <input value={g.label}
                      onChange={(e) => setGroupsDraft((prev) => prev.map((x, i) => (i === gi ? { ...x, label: e.target.value } : x)))}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm font-semibold" />
                    {g.is_default && <span className="text-xs bg-gray-100 text-gray-600 rounded px-2 py-0.5">principal</span>}
                  </div>
                  {g.is_default ? (
                    <p className="text-sm text-gray-500">
                      Tiendas: {stores.filter((s) => !groupsDraft.some((o) => !o.is_default && o.store_ids.includes(s.id))).map((s) => s.name).join(', ') || '—'}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {stores.map((s) => (
                        <label key={s.id} className="inline-flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={g.store_ids.includes(s.id)} onChange={() => toggleStoreInGroup(g.key, s.id)} />
                          {s.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={saveGroups} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Guardar cuentas</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Legado ──────────────────────────────────────────────────────── */}
      {tab === 'legacy' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-1">Registros manuales pendientes (esquema anterior)</h2>
          <p className="text-sm text-gray-500 mb-4">Ventas Cashea cargadas a mano antes del cambio al pago semanal. Se conservan solo para cerrar lo que quede pendiente.</p>
          {loading ? <p className="text-center py-8 text-gray-500">Cargando…</p> : legacy.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No hay registros manuales pendientes.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tienda</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Inicial</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Neto</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {legacy.map((s) => (
                    <tr key={s.id}>
                      <td className="px-3 py-2">{fmtDate(s.date)}</td>
                      <td className="px-3 py-2 text-xs">{stores.find((st) => st.id === s.store_id)?.name || s.store_id}</td>
                      <td className="px-3 py-2">{s.client_name} <span className="text-xs text-gray-500">{s.client_cedula}</span></td>
                      <td className="px-3 py-2 text-right">{fmtUSD(s.total_amount_usd)}</td>
                      <td className="px-3 py-2 text-right">{fmtUSD(s.initial_payment_usd)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-green-700">{fmtUSD(s.net_amount_to_receive_usd)}</td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <button onClick={() => { setLegacyPay(s); setLegacyForm({ payment_date: todayISO(), payment_amount_ves: '', payment_exchange_rate: '' }); }} className="px-2 py-1 bg-green-600 text-white rounded text-xs mr-1">Pagado</button>
                        {isAdmin && <button onClick={() => deleteLegacy(s.id)} className="px-2 py-1 bg-red-600 text-white rounded text-xs">Eliminar</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {legacyPay && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-3">
                <h3 className="font-bold">Marcar pagado: {legacyPay.client_name}</h3>
                <p className="text-sm text-gray-600">Neto esperado: {fmtUSD(legacyPay.net_amount_to_receive_usd)}</p>
                <input type="date" value={legacyForm.payment_date} onChange={(e) => setLegacyForm({ ...legacyForm, payment_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input type="number" step="0.01" placeholder="Monto recibido (Bs)" value={legacyForm.payment_amount_ves} onChange={(e) => setLegacyForm({ ...legacyForm, payment_amount_ves: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input type="number" step="0.01" placeholder="Tasa de cambio" value={legacyForm.payment_exchange_rate} onChange={(e) => setLegacyForm({ ...legacyForm, payment_exchange_rate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setLegacyPay(null)} className="px-3 py-2 border border-gray-300 rounded-md text-sm">Cancelar</button>
                  <button onClick={submitLegacyPayment} disabled={!legacyForm.payment_amount_ves || !legacyForm.payment_exchange_rate} className="px-3 py-2 bg-green-600 text-white rounded-md text-sm disabled:bg-gray-400">Confirmar</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Cashea;
